import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { getReportingTimeZone } from '@/lib/report-timezone';

export interface CcFundingListParams {
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  // JSON-encoded Record<string, string> of per-column filter values, keyed
  // by the same field names used for sortBy.
  filters?: string;
}

export interface CcFundingPagination {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

export interface CcFundingMatchedRow {
  sourceId: string;
  targetId: string;
  approvalCode: string;
  cardLastFour: string;
  approvedAmount: string;
  amount: string;
  localSaleDate: string;
  fundingDate: string;
  lagDays: number;
  invoiceNumber: string | null;
  customerName: string | null;
  orderId: string | null;
  status: string | null;
}

export interface CcFundingUnmatchedSourceRow {
  id: string;
  sheetName: string;
  rowNumber: number;
  approvalCode: string;
  cardLastFour: string;
  approvedAmount: string;
  localSaleDate: string;
  invoiceNumber: string | null;
  customerName: string | null;
}

export interface CcFundingUnmatchedFundingRow {
  id: string;
  sheetName: string;
  rowNumber: number;
  authCode: string;
  lastFour: string;
  amount: string;
  fundingDate: string;
  transactionNumber: string;
  orderId: string | null;
}

export interface CcFundingSourceRow {
  id: string;
  sheetName: string;
  rowNumber: number;
  store: string | null;
  storeAlias: string | null;
  invoiceNumber: string | null;
  customerName: string | null;
  cardType: string | null;
  accountNumberMasked: string | null;
  cardLastFour: string;
  approvedAmount: string;
  approvalCode: string;
  localSaleDate: string;
  batchStatus: string | null;
}

export interface CcFundingTargetRow {
  id: string;
  sheetName: string;
  rowNumber: number;
  transactionNumber: string;
  fundingDate: string;
  method: string | null;
  name: string | null;
  brand: string | null;
  lastFour: string;
  amount: string;
  authCode: string;
  status: string | null;
  orderId: string | null;
}

export interface CcFundingDuplicateSourceRow {
  id: string;
  sheetName: string;
  rowNumber: number;
  approvalCode: string;
  cardLastFour: string;
  approvedAmount: string;
  localSaleDate: string;
  invoiceNumber: string | null;
  customerName: string | null;
  duplicateCount: number;
}

export interface CcFundingVoidedSourceRow {
  id: string;
  sheetName: string;
  rowNumber: number;
  approvalCode: string;
  cardLastFour: string;
  approvedAmount: string;
  localSaleDate: string;
  invoiceNumber: string | null;
  customerName: string | null;
  transType: string | null;
  subType: string | null;
}

export interface CcFundingSummary {
  matched: { count: number; total: string };
  unmatchedSource: { count: number; total: string };
  unmatchedFunding: { count: number; total: string };
  duplicates: { count: number; total: string };
}

interface CcFundingListResponse<T> {
  data: T[];
  pagination: CcFundingPagination;
  range: Record<string, unknown>;
}

function useCcFundingReport<T>(key: string, path: string, params: CcFundingListParams, enabled = true) {
  // Bucket dateFrom/dateTo by the business-local day, not UTC (see the
  // SuperNova hook / dateRangeConds). Keyed so a zone change refetches.
  const timeZone = getReportingTimeZone();
  return useQuery<CcFundingListResponse<T>>({
    queryKey: ['cc-funding', key, params, timeZone],
    queryFn: () => api.get(path, { ...params, timeZone } as Record<string, string | number | undefined>),
    enabled,
  });
}

export const useCcFundingMatched = (params: CcFundingListParams, enabled = true) =>
  useCcFundingReport<CcFundingMatchedRow>('matched', '/cc-funding/report/matched', params, enabled);

export const useCcFundingUnmatchedSource = (params: CcFundingListParams, enabled = true) =>
  useCcFundingReport<CcFundingUnmatchedSourceRow>(
    'unmatched-source',
    '/cc-funding/report/unmatched-source',
    params,
    enabled,
  );

export const useCcFundingUnmatchedFunding = (params: CcFundingListParams, enabled = true) =>
  useCcFundingReport<CcFundingUnmatchedFundingRow>(
    'unmatched-funding',
    '/cc-funding/report/unmatched-funding',
    params,
    enabled,
  );

export const useCcFundingSourceRows = (params: CcFundingListParams, enabled = true) =>
  useCcFundingReport<CcFundingSourceRow>('source-rows', '/cc-funding/report/source-rows', params, enabled);

export const useCcFundingTargetRows = (params: CcFundingListParams, enabled = true) =>
  useCcFundingReport<CcFundingTargetRow>('funding-rows', '/cc-funding/report/funding-rows', params, enabled);

export const useCcFundingDuplicateSource = (params: CcFundingListParams, enabled = true) =>
  useCcFundingReport<CcFundingDuplicateSourceRow>(
    'duplicate-source',
    '/cc-funding/report/duplicate-source',
    params,
    enabled,
  );

export const useCcFundingVoidedSource = (params: CcFundingListParams, enabled = true) =>
  useCcFundingReport<CcFundingVoidedSourceRow>('voided', '/cc-funding/report/voided', params, enabled);

export const useCcFundingVoidedRecapturedSource = (params: CcFundingListParams, enabled = true) =>
  useCcFundingReport<CcFundingVoidedSourceRow>(
    'voided-recaptured',
    '/cc-funding/report/voided-recaptured',
    params,
    enabled,
  );

export function useCcFundingSummary(params: Omit<CcFundingListParams, 'page' | 'perPage'>) {
  const timeZone = getReportingTimeZone();
  return useQuery<{ data: CcFundingSummary }>({
    queryKey: ['cc-funding', 'summary', params, timeZone],
    queryFn: () => api.get('/cc-funding/report/summary', { ...params, timeZone } as Record<string, string | undefined>),
  });
}

export interface UploadCcFundingResult {
  uploadId: string;
  fileName: string;
  sheetsProcessed: string[] | number;
  skippedSheets?: string[];
  sourceRowsInserted: number;
  fundingRowsInserted: number;
  sourceRowsSkipped?: number;
  fundingRowsSkipped?: number;
  rowErrors: { sheet: string; rowNumber: number; message: string }[];
  rowErrorsTruncated?: boolean;
  duplicate: boolean;
}

export function useUploadCcFundingWorkbook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ locationId, file }: { locationId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`/api/v1/cc-funding/upload?locationId=${encodeURIComponent(locationId)}`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: { message: 'Upload failed' } }));
        throw error;
      }
      const body = await response.json();
      return body.data as UploadCcFundingResult;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['cc-funding'] });
    },
  });
}
